import React, { memo, useCallback, useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { ToastWithProgress } from '@/feature/notifications/components/toast_with_progress';
import { MAX_PERSISTED_NOTIFICATIONS, useNotificationStore } from '../notifications_store';
import { DEFAULT_TOAST_DURATION, TOAST_DURATION_OPTIONS } from '@/common/timeout_constants';
import type { NotificationPosition } from '@/feature/notifications/components/toast_with_progress.tsx';
import type { PersistentNotification } from '../types.ts';

const MAX_VISIBLE_TOASTS = 10;
const TOP_OFFSET = 64;

const getPositionStyles = (position: NotificationPosition) => {
  const commonStyles = {
    position: 'fixed' as const,
    zIndex: 1400,
  };
  const margin = 24;

  switch (position) {
    case 'top-right':
      return { ...commonStyles, top: TOP_OFFSET, right: margin };
    case 'top-center':
      return { ...commonStyles, top: TOP_OFFSET, left: '50%', transform: 'translateX(-50%)' };
    case 'bottom-right':
      return { ...commonStyles, bottom: margin, right: margin };
    default:
      return { ...commonStyles, top: TOP_OFFSET, right: margin };
  }
};

export const NotificationsContainer = memo(function NotificationsContainer() {
  const notifications = useNotificationStore((s) => s.list);
  const position = useNotificationStore((s) => s.position);
  const showToasts = useNotificationStore((s) => s.showToasts);
  const toastDuration = useNotificationStore((s) => s.toastDuration);
  const markRead = useNotificationStore((s) => s.markRead);
  const markManyRead = useNotificationStore((s) => s.markManyRead);
  const [toastQueue, setToastQueue] = useState<PersistentNotification[]>([]);
  const autoHideDuration = TOAST_DURATION_OPTIONS[toastDuration] ?? TOAST_DURATION_OPTIONS[DEFAULT_TOAST_DURATION];

  useEffect(() => {
    const notificationIdSet = new Set(notifications.map((notification) => notification.id));

    // Remove superseded notifications, and (when toasts globally muted) any non-critical entries from the queue
    const filteredQueue = toastQueue.filter((toast) => {
      if (!notificationIdSet.has(toast.id)) return false;
      const notificationInStore = notifications.find((n) => n.id === toast.id);
      if (!notificationInStore || notificationInStore.superseded) return false;
      if (!showToasts && !notificationInStore.criticalOverride) return false;
      return true;
    });

    const queueIdSet = new Set(filteredQueue.map((toast) => toast.id));

    // Find unread, non-superseded notifications not in queue (oldest-to-newest).
    // When the global toggle is off, only criticalOverride entries are allowed through.
    const newlyQueued = notifications
      .filter((n) => !n.read && !queueIdSet.has(n.id) && !n.superseded && (showToasts || n.criticalOverride === true))
      .reverse();

    if (newlyQueued.length === 0 && filteredQueue.length === toastQueue.length) {
      return;
    }

    const nextQueue = [...filteredQueue, ...newlyQueued].slice(-MAX_PERSISTED_NOTIFICATIONS);

    if (nextQueue.length !== toastQueue.length || nextQueue.some((toast, index) => toast.id !== toastQueue[index]?.id)) {
      setToastQueue(nextQueue);
    }

    if (newlyQueued.length) {
      markManyRead(newlyQueued.map((notification) => notification.id));
    }
  }, [notifications, toastQueue, showToasts, markManyRead]);

  const removeToast = useCallback(
    (id: string) => {
      setToastQueue((currentQueue) => currentQueue.filter((toast) => toast.id !== id));
      markRead(id);
    },
    [markRead]
  );

  const visibleToasts = toastQueue.slice(0, MAX_VISIBLE_TOASTS);

  if (!visibleToasts.length) return null;

  return (
    <Stack spacing={1} direction={position === 'bottom-right' ? 'column-reverse' : 'column'} sx={getPositionStyles(position)}>
      {visibleToasts.map((toast) => (
        <ToastWithProgress
          key={toast.id}
          notification={toast}
          onClose={() => removeToast(toast.id)}
          position={position}
          autoHideDuration={autoHideDuration}
        />
      ))}
    </Stack>
  );
});
